<template>
  <div class="border border-brand_gray1 p-4 col-span-4 rounded-md">
    <!-- Forms Section  -->
    <div class="w-full flex-col">
      <!-- Form Group 1 -->
      <div class="flex center-all w-full mb-2">
        <!-- Invoice Number -->
        <div class="flex flex-col w-full p-1">
          <div class="flex center-y">
            <label
              class="w-1/3 text-brand_gray3 block tracking-wide text-base mb-2"
            >
              Invoice Number :
            </label>

            <!-- Readonly if selectedInvoice exists -->
            <InputText
              v-if="selectedInvoice || editable"
              :value="selectedInvoice.invoice_number || null"
              readonly
              class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
            />

            <!-- Editable -->
            <InputText
              v-else
              v-model="formData.invoice_number"
              type="text"
              class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
              placeholder="Invoice Number"
            />
          </div>

          <!-- Error Message -->
          <span
            v-if="errors && errors.invoice_number"
            class="text-red-500 text-sm ml-[33%] mt-1"
          >
            {{ errors.invoice_number }}
          </span>
        </div>

        <!-- Date -->
        <div class="flex flex-col w-full p-1">
          <div class="flex center-y">
            <label
              class="w-1/3 text-brand_gray3 block tracking-wide text-base mb-2"
            >
              Date :
            </label>

            <!-- Readonly if selectedInvoice exists -->
            <InputText
              v-if="selectedInvoice"
              :value="formatIso(selectedInvoice.invoice_date) || null"
              readonly
              class="w-7/12 bg-gray-200 text-gray-700 rounded-md"
            />

            <!-- Editable DatePicker -->
            <DatePicker
              v-else
              v-model="formData.invoice_date"
              placeholder="Date"
              class="w-7/12 text-gray-700 rounded-md"
            />
          </div>

          <!-- Error Message -->
          <span
            v-if="errors && errors.invoice_date"
            class="text-red-500 text-sm ml-[33%] mt-1"
          >
            {{ errors.invoice_date }}
          </span>
        </div>
      </div>

      <!-- Form Group 2 -->
      <div class="flex center-all w-full mb-2">
        <!-- Sold To -->
        <div class="flex flex-col w-full p-1">
          <div class="flex center-y">
            <label
              class="w-1/3 text-brand_gray3 block tracking-wide text-base mb-2"
            >
              Sold To :
            </label>

            <InputText
              v-if="selectedInvoice"
              :value="selectedInvoice?.payee?.sold_to || ''"
              readonly
              class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
            />

            <AutoComplete
              v-else
              :loading="false"
              v-model="formData.soldTo"
              :minLength="3"
              :suggestions="suggestions"
              :emptyMessage="'No Companies found'"
              optionLabel="Companies"
              @item-select="handleCompanySelect"
              @complete="searchCompany"
              @clear="handleCompanyClear"
              class="text-gray-700 w-7/12 rounded-md"
              placeholder="Sold To"
              :inputClass="'w-full bg-gray-200 text-gray-700 rounded-md py-3 px-4'"
            />
          </div>

          <!-- Error Message -->
          <span
            v-if="errors && errors.soldTo"
            class="text-red-500 text-sm ml-[33%] mt-1"
          >
            {{ errors.soldTo }}
          </span>
        </div>

        <!-- Terms -->
        <div class="flex center-y w-full p-1">
          <label
            class="w-1/3 text-brand_gray3 block tracking-wide text-base mb-2"
          >
            Terms :
          </label>

          <InputText
            v-if="selectedInvoice"
            :value="selectedInvoice?.payee?.terms || ''"
            readonly
            class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
          />

          <InputText
            v-else
            v-model="formData.terms"
            type="text"
            class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
            placeholder="Terms"
          />
        </div>
      </div>

      <!-- Form Group 3 -->
      <div class="flex center-all w-full mb-2">
        <!-- TIN -->
        <div class="flex flex-col w-full p-1">
          <div class="flex center-y">
            <label
              class="w-1/3 text-brand_gray3 block tracking-wide text-base mb-2"
            >
              TIN :
            </label>

            <InputText
              v-if="selectedInvoice"
              :value="selectedInvoice?.payee?.tin || ''"
              readonly
              class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
            />

            <InputText
              v-else
              v-model="formattedTIN"
              type="text"
              class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
              placeholder="TIN No. Or 0 if not applicable"
            />
          </div>

          <!-- Error Message -->
          <span
            v-if="errors && errors.tin"
            class="text-red-500 text-sm ml-[33%] mt-1"
          >
            {{ errors.tin }}
          </span>
        </div>

        <!-- OSCA/PWD ID No -->
        <div class="flex center-y w-full p-1">
          <label
            class="w-1/3 text-brand_gray3 block tracking-wide text-base mb-2"
          >
            OSCA/PWD ID No :
          </label>

          <InputText
            v-if="selectedInvoice"
            :value="selectedInvoice?.payee?.osca_pwd_id_no || ''"
            readonly
            class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
          />

          <InputText
            v-else
            v-model="formData.osca_pwd_id_no"
            type="text"
            class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
            placeholder="OSCA/PWD ID No"
          />
        </div>
      </div>

      <!-- Form Group 4 -->
      <div class="flex center-all w-full mb-2">
        <!-- Address -->
        <div class="flex flex-col w-full p-1">
          <div class="flex center-y">
            <label
              class="w-1/3 text-brand_gray3 block tracking-wide text-base mb-2"
            >
              Address :
            </label>

            <InputText
              v-if="selectedInvoice"
              :value="selectedInvoice?.payee?.address || ''"
              readonly
              class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
            />

            <InputText
              v-else
              v-model="formData.address"
              type="text"
              class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
              placeholder="Address"
            />
          </div>

          <!-- Error Message -->
          <span
            v-if="errors && errors.address"
            class="text-red-500 text-sm ml-[33%] mt-1"
          >
            {{ errors.address }}
          </span>
        </div>

        <!-- SC:PWD Signature -->
        <div class="flex center-y w-full p-1">
          <label
            class="w-1/3 text-brand_gray3 block tracking-wide text-base mb-2"
          >
            SC:PWD Signature :
          </label>

          <InputText
            v-if="selectedInvoice"
            :value="selectedInvoice?.payee?.sc_pwd_signature || ''"
            readonly
            class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
          />

          <InputText
            v-else
            v-model="formData.sc_pwd_signature"
            type="text"
            class="w-7/12 bg-gray-200 text-gray-700 rounded-md py-3 px-4"
            placeholder="SC:PWD Signature"
          />
        </div>
      </div>

      <p class="text-lg font-semibold mt-10 text-brand_gray3">Items :</p>

      <!-- Items Section -->
      <hr class="my-3" />

      <!-- Add item Forms -->
      <div v-if="!selectedInvoice?.invoiceItems" class="flex w-full mb-2 gap-2">
        <!-- Description -->
        <div class="flex flex-col flex-[3] p-1">
          <label class="text-brand_gray3 block tracking-wide text-base mb-2">
            Description :
          </label>
          <InputText
            v-model="formData.invoice_items.description"
            class="w-full bg-gray-200 text-gray-700 rounded-md py-3 px-4 leading-tight focus:outline focus:bg-white"
            type="text"
            placeholder="Description"
          />
        </div>

        <!-- Quantity -->
        <div class="flex flex-col flex-[1] p-1">
          <label class="text-brand_gray3 block tracking-wide text-base mb-2">
            Quantity :
          </label>
          <InputText
            v-model="formData.invoice_items.quantity"
            class="w-full bg-gray-200 text-gray-700 rounded-md py-3 px-4 leading-tight focus:outline focus:bg-white"
            type="number"
            placeholder="Quantity"
          />
        </div>

        <!-- Cost -->
        <div class="flex flex-col flex-[1] p-1">
          <label class="text-brand_gray3 block tracking-wide text-base mb-2">
            Cost :
          </label>
          <InputText
            v-model="formData.invoice_items.cost"
            class="w-full bg-gray-200 text-gray-700 rounded-md py-3 px-4 leading-tight focus:outline focus:bg-white"
            type="number"
            placeholder="Cost"
          />
        </div>

        <!-- Total -->
        <div class="flex flex-col flex-[1] p-1">
          <label class="text-brand_gray3 block tracking-wide text-base mb-2">
            Total :
          </label>
          <InputText
            disabled
            class="w-full bg-gray-200 text-gray-700 rounded-md py-3 px-4 leading-tight focus:outline focus:bg-white"
            type="number"
            placeholder="Total"
          />
        </div>

        <!-- Add Button -->
        <div class="flex items-end">
          <button
            @click.prevent="
              addItem(
                formData.invoice_items.description,
                formData.invoice_items.quantity,
                formData.invoice_items.cost
              )
            "
            class="py-3 px-4 mb-1 bg-brand_orange2 hover:bg-brand_orange3 rounded-md"
          >
            <addSVG class="w-4 h-4 text-brand_gray3" />
          </button>
        </div>
      </div>
    </div>
    <!-- Table Section -->
    <div class="overflow-auto h-2/5 mt-8">
      <div
        class="w-full text-brand_gray3 text-center"
        v-if="
          !selectedInvoice?.invoiceItems && formData.invoice_items.length === 0
        "
      >
        <!-- Show array-level error if exists -->
        <span
          v-if="errors && errors.invoice_items"
          class="text-red-500 text-sm block mb-1"
        >
          {{ errors.invoice_items }}
        </span>

        <p>No items added yet.</p>
      </div>

      <table
        v-if="selectedInvoice?.invoiceItems"
        class="w-full border border-gray-300 rounded-md"
      >
        <thead class="bg-gray-100">
          <tr>
            <th
              class="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b"
            >
              Description
            </th>
            <th
              class="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b"
            >
              Quantity
            </th>
            <th
              class="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b"
            >
              Cost
            </th>
            <th
              class="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b"
            >
              Total
            </th>
            <th
              class="px-4 py-2 text-center text-sm font-medium text-gray-700 border-b"
            ></th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(item, index) of selectedInvoice?.invoiceItems"
            :key="index"
          >
            <!-- Description -->
            <td class="py-2 border-b w-full align-top">
              <span
                class="block text-brand_gray3 rounded-md py-2 px-3 whitespace-normal break-words"
              >
                {{ item.description }}
              </span>
            </td>

            <!-- Quantity -->
            <td class="px-4 py-2 border-b w-24">
              <span class="block w-24 text-brand_gray3 rounded-md py-2 px-1">
                {{ item.quantity }}
              </span>
            </td>

            <!-- Cost -->
            <td class="px-4 py-2 border-b w-24">
              <span class="block w-24 text-brand_gray3 rounded-md py-2 px-1">
                {{ item.cost }}
              </span>
            </td>

            <!-- Total -->
            <td class="px-4 py-2 border-b w-28">
              <span class="block w-24 text-brand_gray3 rounded-md py-2 px-1">
                {{ item.total }}
              </span>
            </td>

            <!-- Action -->
            <td class="px-4 py-2 text-center flex gap-2">
              <button
                @click.prevent="deleteItem(index)"
                class="flex items-center gap-2 px-4 py-2 hover:bg-brand_orange1 text-white rounded-md"
              >
                <deleteSVG class="w-5 h-5 text-brand_gray3" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <table
        v-if="formData.invoice_items.length !== 0"
        class="full border border-gray-300 rounded-md"
      >
        <thead class="bg-gray-100">
          <tr>
            <th
              class="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b"
            >
              Description
            </th>
            <th
              class="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b"
            >
              Quantity
            </th>
            <th
              class="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b"
            >
              Cost
            </th>
            <th
              class="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b"
            >
              Total
            </th>
            <th
              class="px-4 py-2 text-center text-sm font-medium text-gray-700 border-b"
            ></th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, index) of formData.invoice_items" :key="index">
            <!-- Description -->
            <td class="py-2 border-b w-full">
              <span class="block w-full text-brand_gray3 rounded-md py-2 px-3">
                {{ item.description }}
              </span>
            </td>

            <!-- Quantity -->
            <td class="px-4 py-2 border-b w-24">
              <span class="block w-24 text-brand_gray3 rounded-md py-2 px-1">
                {{ item.quantity }}
              </span>
            </td>

            <!-- Cost -->
            <td class="px-4 py-2 border-b w-24">
              <span class="block w-24 text-brand_gray3 rounded-md py-2 px-1">
                {{ item.cost }}
              </span>
            </td>

            <!-- Total -->
            <td class="px-4 py-2 border-b w-28">
              <span class="block w-24 text-brand_gray3 rounded-md py-2 px-1">
                {{ item.items_total }}
              </span>
            </td>

            <!-- Action -->
            <td class="px-4 py-2 text-center flex gap-2">
              <button
                @click.prevent="deleteItem(index)"
                class="flex items-center gap-2 px-4 py-2 hover:bg-brand_orange1 text-white rounded-md"
              >
                <deleteSVG class="w-5 h-5 text-brand_gray3" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { reactive, watch, inject, ref, computed } from "vue";

// SVG Imports
import addSVG from "../../svg/addSvgIcon.vue";
import deleteSVG from "../../svg/deleteSvgIcon.vue";

export default {
  components: {
    addSVG,
    deleteSVG,
  },

  props: {
    selectedInvoice: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
    },
    prefillData: {
      type: Object,
    },
  },

  setup(props, { emit }) {
    const axios = inject("$axios");

    const formData = reactive({
      invoice_number: null,
      soldTo: null,
      tin: null,
      address: null,
      terms: null,
      osca_pwd_id_no: null,
      sc_pwd_signature: null,
      invoice_items: [],
      invoice_date: null,
    });

    const suggestions = ref([]);

    const today = new Date();
    const formattedToday = today.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    // Step 2: Watch form changes to emit
    watch(
      formData,
      (newVal) => {
        emit("dataEntry", { ...newVal });
      },
      { deep: true }
    );

    // Step 3: Watch props.prefillData
    watch(
      () => props.prefillData,
      (newVal) => {
        if (newVal) {
          Object.assign(formData, newVal);
        }
      },
      { deep: true, immediate: true }
    );

    // Methods Section

    const addItem = async (
      itemDescription = "",
      itemQuantity = 0,
      itemCost = 0
    ) => {
      const newItem = {
        description: itemDescription,
        quantity: itemQuantity,
        cost: itemCost,
        items_total: itemQuantity * itemCost,
      };

      if (newItem.description === "") {
      } else {
        formData.invoice_items.push(newItem);
      }

      console.log("Item added:", newItem);
    };

    const deleteItem = (index) => {
      formData.invoice_items.splice(index, 1);
      console.log(formData.invoice_items);
    };

    // Move this to utils

    const formatIso = (isoString) => {
      const d = new Date(isoString);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = String(d.getFullYear()).slice(-2);

      return `${month}/${day}/${year}`;
    };

    const searchCompany = async (event) => {
      console.log(event);
      const query = event.query?.trim();

      if (!query) {
        suggestions.value = [];
        return;
      }

      try {
        const res = await axios.get("/api/invoice/get/company", {
          params: { name: query },
        });

        suggestions.value = res.data.data || [];
        console.log(suggestions.value);
      } catch (error) {
        console.error("Error searching company:", error);
        suggestions.value = [];
      }
    };

    const handleCompanySelect = async (event) => {
      formData.soldTo = event.value.Companies;

      try {
        const res = await axios.get("/api/invoice/get/company/auto", {
          params: { name: formData.soldTo },
        });
        if (res.data && res.data.data) {
          // Use the matched payee data
          const matchedPayee = res.data.data;
          console.log("Matched payee record:", matchedPayee);

          formData.tin = matchedPayee.tin;
          formData.address = matchedPayee.address;
        }
      } catch (error) {
        console.error("Error fetching company match:", error);
      }
    };

    const handleCompanyClear = () => {
      formData.tin = "";
      formData.soldTo = "";
      formData.address = "";
      console.log("Company input cleared");
    };

    function formatTIN(value) {
      if (!value) return "";

      const digits = value.replace(/\D/g, "");

      if (digits.length <= 9) {
        return digits.replace(/(\d{3})(\d{3})(\d{0,3})/, (_, p1, p2, p3) =>
          [p1, p2, p3].filter(Boolean).join("-")
        );
      } else {
        return digits.replace(
          /(\d{3})(\d{3})(\d{3})(\d{0,3})/,
          (_, p1, p2, p3, p4) => [p1, p2, p3, p4].filter(Boolean).join("-")
        );
      }
    }

    const formattedTIN = computed({
      get() {
        return formatTIN(formData.tin);
      },
      set(value) {
        formData.tin = value;
      },
    });

    return {
      formData,
      suggestions,
      formattedTIN,
      today,
      formattedToday,
      addItem,
      formatTIN,
      formatIso,
      deleteItem,
      searchCompany,
      handleCompanyClear,
      handleCompanySelect,
    };
  },
};
</script>
